import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DialogModel} from '@shared/models/dialog.model';
import {getAllCountries, ParticleState} from '@shared/store/reducers/particle.reduer';
import {take, takeUntil} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {Subject} from 'rxjs';
import {DialogService} from '@shared/services/dialog-service';
import {OrbitaxExcelMemoService} from '../../orbitax-excel-memo.service';
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import {ActivatedRoute, Router} from '@angular/router';
import {BreadcrumbModel} from '@shared/models/breadcrumb.model';
import {viewMemoTitle} from '../../orbitax-view-memo.constants';
import { IPlatformToolComponent, IPlatformToolHandler } from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import { PlatformCommand, PlatformCommandResponse } from '@shared/commands/command-base';
import { InvalidateSubject } from '@shared/helpers/invalidate.subject';
import { MagicLinkModel, TabModel } from '@shared/models/platform-tool-models';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { CommandService } from '@core/services/command.service';
import { PlatformQuery } from '@shared/models/platform-query/platform-query';
import { MemoInfo } from '@features/excel-memo/models/memo-info';
import { CorporateTaxRateMemoModel } from '@features/excel-memo/models/corporate-tax-memo.model';

@Component({
    standalone: false,
    selector: 'particle-corporate-tax-view-memo',
  templateUrl: './corporate-tax-view-platform-memo.component.html',
  styleUrls: ['./corporate-tax-view-memo.component.scss']
})
export class CorporateTaxPlatformMemoContentComponent implements OnInit, OnDestroy, IPlatformToolComponent {
  handler: IPlatformToolHandler;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;
  invalidateSubject: InvalidateSubject;
  @Input() id: string;
  inputForm: FormGroup;
  private allCountries: any[];
  private countryCode: any;
  unsubscribe$ = new Subject<void>();
  private memoInfo: any;
  private corporateTaxMemoInfo: CorporateTaxRateMemoModel;
  breadcrumbs: any[];
  corporateTaxSubTypes = ['statutoryTaxRate','surtaxBasedonStatutoryTax', 'surtaxBasedonTaxableIncome','surtaxDeductiblefromTaxableIncome'];
  api = 'ITRCEApi';

  constructor(private formBuilder: FormBuilder,
              private particleStore: Store<ParticleState>,
              private dialogService: DialogService,
              private activatedRoute: ActivatedRoute,
              private excelMemoService: OrbitaxExcelMemoService,
              private router: Router,
              private fuseSplashScreenService: FuseSplashScreenService,
              private queryService: PlatformQueryService,
              private commandService: CommandService) {
    this.inputForm = this.formBuilder.group({
      country: ['', Validators.required],
      taxableIncome: ['100', Validators.required],
      statutoryTaxRate: ['0', Validators.required],
      surtaxBasedonStatutoryTax: ['0', Validators.required],
      surtaxBasedonTaxableIncome: ['0', Validators.required],
      surtaxDeductiblefromTaxableIncome: ['0', Validators.required],
      isCustom: [false]
    });
  }
  onBeforeExecuteNextCommand(): boolean {
    throw new Error('Method not implemented.');
  }
  onAfterExecuteNextCommand(nextCommandResponse: PlatformCommandResponse) {
    throw new Error('Method not implemented.');
  }
  onBeforeExecutePreviousCommand(): boolean {
    throw new Error('Method not implemented.');
  }
  onAfterExecutePreviousCommand(previousCommandResponse: PlatformCommandResponse) {
    throw new Error('Method not implemented.');
  }
  ngOnInit() {

  }

  onLoad() {
    this.id = this.magicLinkModel.fieldValues['MemoId'];
    this.particleStore
    .pipe(select(getAllCountries))
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((country) => {
      if(country) {
        this.allCountries = country;
        this.getCorporateTaxMemo(this.id);
      }
    });
  }

  private getCorporateTaxMemo(id: string) {
    const query = new PlatformQuery();
    query.name = 'RateMemoQuery';
    query.api = this.api;
    query.projectId = '[PROJECT_ID]';
    query.fieldValues['MemoId'] = this.id;
    this.queryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.memoInfo = response.fieldValues['MemoModel'];
      this.corporateTaxMemoInfo = this.memoInfo.corporateTaxMemo;
      this.refreshMemo();
      this.subscribeOnChange();
    })
  }

  private refreshMemo() {
    if (this.corporateTaxMemoInfo.countryCode) {
      this.countryCode = this.corporateTaxMemoInfo.countryCode;
      this.inputForm.controls['country'].setValue(this.getCountryFromCountryCode(this.countryCode).displayName);
    }
    this.syncCorporateTaxSubTypesEditedStatus(this.corporateTaxMemoInfo.isCustom);
    this.syncCorporateTaxSubTypes();
    this.fuseSplashScreenService.hide();
  }

  syncCorporateTaxSubTypesEditedStatus(canEdit: boolean) {
    this.corporateTaxSubTypes.forEach(item => {
      canEdit ?
      this.inputForm.controls[item].enable() :
      this.inputForm.controls[item].disable();
    });
  }

  subscribeOnChange() {
    this.inputForm.controls['isCustom'].valueChanges.subscribe(() => {
        const isCustom = this.inputForm.controls['isCustom'].value;
        this.syncCorporateTaxSubTypesEditedStatus(isCustom);
        if(isCustom != this.corporateTaxMemoInfo.isCustom) {
          this.updateMemoField('IsCustom', isCustom);
        }
    });
  }

  updateMemoField(key: string, value: any) {
    const memoInfo: MemoInfo = {id: this.id, type: 'ct'};
    const editedField = {key, value};
    this.excelMemoService.updateMemoField(memoInfo, this.magicLinkModel, editedField, (response) => {
      this.memoInfo = response.fieldValues['MemoModel'];
      this.corporateTaxMemoInfo = this.memoInfo.corporateTaxMemo;
      if(response.fieldValues['RefreshBreadcrumbBar']) {
        this.handler.valueChanged({action: 'Refresh', name: 'RefreshBreadcrumbBar'});
      }
      if(key == 'CountryCode') {
        this.syncCorporateTaxSubTypes();
      }
    });
  }

  syncCorporateTaxSubTypes() {
    this.inputForm.controls['isCustom'].setValue(this.corporateTaxMemoInfo.isCustom);
    this.inputForm.controls['taxableIncome'].setValue(this.corporateTaxMemoInfo.taxableIncome);
    this.inputForm.controls['statutoryTaxRate'].setValue(this.round(this.corporateTaxMemoInfo.statutoryTaxRate));
    this.inputForm.controls['surtaxBasedonStatutoryTax'].setValue(this.round(this.corporateTaxMemoInfo.surtaxBasedOnStatutoryTax));
    this.inputForm.controls['surtaxBasedonTaxableIncome'].setValue(this.round(this.corporateTaxMemoInfo.surtaxBasedOnTaxableIncome));
    this.inputForm.controls['surtaxDeductiblefromTaxableIncome'].setValue(this.round(this.corporateTaxMemoInfo.surtaxDeductibleFromTaxableIncome));
  }

  public updateMemo(key: string) {
    const value = this.inputForm.controls[key].value;
    this.updateMemoField(this.capitalize(key), value);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private round(value: number) {
    return Math.round(value * 10000) / 100;
  }

  showCountryDialogue() {
    this.excelMemoService.showCountryDialgogue('Country', this.countryCode, (selectedCountry) => {
      if(selectedCountry.code !== this.countryCode) {
        this.countryCode = selectedCountry.code;
        if(selectedCountry && selectedCountry.displayName) {
          this.inputForm.controls['country'].setValue(selectedCountry.displayName);
          this.updateMemoField('CountryCode', selectedCountry.code);
        }
      }
    });
  }

  capitalize(key: string): string {
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  private getCountryFromCountryCode(countryCode: string) {
      return this.allCountries.find(t => t.code === countryCode);
  }
}
